'use client';
import React from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { SectionTitle } from '@benzinga/core-ui';
import { numberShorthand } from '@benzinga/utils/numberUtils';
import { sanitizeHTML } from '@benzinga/frontend-utils';

import type { BaseQuotePageProps, BaseQuoteProfile } from '../../../../../src/utils/quote/types';
import type { ShortInterestSummary } from '../../../../../src/utils/quote/summaries';

import ShortInterestSummaryBox from '../../../../../src/components/Quote/ShortInterestSummaryBox';
import { ShortInterestHistoryTable } from '../../../../../src/components/Quote/ShortInterestHistoryTable';
import DrilldownFooter from '../../../../../src/components/Quote/DrilldownFooter';
import { QuoteFooter } from '../../../../../src/components/Quote/QuoteFooter';
import SeeMoreLink from '../_components/SeeMoreLink';
import { seeMoreDestinations } from '../_utils/seeMoreDestinations';

interface ShortInterestContentProps extends BaseQuotePageProps {
  shortInterestSummary: ShortInterestSummary | null;
}

const generateShortInterestPerformanceDescription = (
  t: TFunction<readonly ['quote', 'common'], undefined>,
  {
    profile,
    symbol,
    shortInterestSummary,
  }: {
    profile: BaseQuoteProfile;
    symbol: string;
    shortInterestSummary: ShortInterestSummary | null;
  },
): string | null => {
  const mostRecentShortInterest = shortInterestSummary?.shortInterest?.[0];
  const name = profile?.richQuoteData?.companyStandardName ?? symbol;
  const shortPercentOfFloat = mostRecentShortInterest?.shortPercentOfFloat ?? '-';
  const shortInterestPriorMonthAsNumber = parseInt(mostRecentShortInterest?.shortPriorMo ?? '');
  const shortInterestPriorMonth = shortInterestPriorMonthAsNumber
    ? numberShorthand(shortInterestPriorMonthAsNumber, 2)
    : '-';
  const shortInterestAsNumber = parseInt(mostRecentShortInterest?.totalShortInterest ?? '');
  const shortInterest = shortInterestAsNumber ? numberShorthand(shortInterestAsNumber, 2) : '-';

  if (!shortInterestPriorMonthAsNumber || !shortInterestAsNumber) {
    return null;
  }

  const shortInterestTrend =
    shortInterestPriorMonthAsNumber && shortInterestAsNumber
      ? shortInterestAsNumber > shortInterestPriorMonthAsNumber
        ? t('Quote.ShortInterest.increased')
        : t('Quote.ShortInterest.decreased')
      : '-';
  const shortInterestRise =
    shortInterestPriorMonthAsNumber && shortInterestAsNumber
      ? shortInterestAsNumber > shortInterestPriorMonthAsNumber
        ? t('Quote.ShortInterest.rising')
        : t('Quote.ShortInterest.falling')
      : '-';

  const ticker = profile?.richQuoteData?.bzExchange ? `${profile?.richQuoteData?.bzExchange}:${symbol}` : symbol;
  return t('Quote.ShortInterest.short-interest-performance', {
    companyName: name,
    rise: shortInterestRise,
    shortInterest,
    shortInterestPriorMonth,
    shortPercentOfFloat,
    ticker,
    trend: shortInterestTrend,
  });
};

const generateShortInterestVolumeDescription = (
  t: TFunction<readonly ['quote', 'common'], undefined>,
  {
    shortInterestSummary,
  }: {
    shortInterestSummary: ShortInterestSummary | null;
  },
): string | null => {
  const mostRecentShortInterest = shortInterestSummary?.shortInterest?.[0];
  const volume = mostRecentShortInterest?.averageDailyVolume
    ? numberShorthand(mostRecentShortInterest?.averageDailyVolume, 2)
    : null;
  const daysToCover = mostRecentShortInterest?.daysToCover || null;

  if (!volume && !daysToCover) {
    return null;
  }

  return t('Quote.ShortInterest.short-interest-volume', {
    daysToCover: daysToCover || '-',
    volume: volume || '-',
  });
};

const ShortInterestContent: React.FC<ShortInterestContentProps> = ({
  metaProps,
  profile,
  symbol,
  shortInterestSummary,
}) => {
  const { t } = useTranslation(['quote', 'common']);

  const shortInterestPerformanceDescription = generateShortInterestPerformanceDescription(t, {
    profile,
    symbol,
    shortInterestSummary,
  });
  const shortInterestVolumeDescription = generateShortInterestVolumeDescription(t, {
    shortInterestSummary,
  });

  return (
    <>
      <div className="main-div pt-0 flex flex-col gap-y-8">
        <div className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between gap-2">
            <SectionTitle level={1} uppercase={false}>
              {metaProps.title}
            </SectionTitle>
            {(shortInterestSummary?.shortInterest?.length ?? 0) > 0 && (
              <SeeMoreLink
                action="see_more_short_interest"
                href={seeMoreDestinations.shortInterest(symbol)}
                label={t('Quote.SeeMore.short-interest')}
              />
            )}
          </div>
          {shortInterestPerformanceDescription && (
            <p
              className="section-description"
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(shortInterestPerformanceDescription) }}
            />
          )}
          {shortInterestVolumeDescription && (
            <p
              className="section-description"
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(shortInterestVolumeDescription) }}
            />
          )}
        </div>
        {shortInterestSummary && <ShortInterestSummaryBox shortInterestSummary={shortInterestSummary} />}
        <ShortInterestHistoryTable rows={shortInterestSummary?.shortInterest ?? []} />
        <DrilldownFooter
          isETF={profile.isETF}
          leftSection={
            <p className="text-sm ml-6">
              {t('Quote.ShortInterest.looking-for-the')}{' '}
              <a href="/short-interest/most-shorted">{t('Quote.ShortInterest.most-shorted-stocks')}</a>?
            </p>
          }
          peers={profile.peers}
          peerSymbols={profile.peerSymbols}
          symbol={symbol}
          type={profile.schedule?.type ?? null}
        />
        <QuoteFooter activeTab="short-interest" showBrowseLink={false} />
      </div>
    </>
  );
};

export default ShortInterestContent;
